import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Lenis from 'lenis';
import '@/lib/i18n';
import '@/App.css';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import Loader from '@/components/layout/Loader';
import CustomCursor from '@/components/layout/CustomCursor';
import CookieConsent from '@/components/layout/CookieConsent';
import PageTransition from '@/components/layout/PageTransition';
import { trackPageView } from '@/lib/analytics';

const Home = lazy(() => import('@/pages/Home'));
const Work = lazy(() => import('@/pages/Work'));
const CaseDetail = lazy(() => import('@/pages/CaseDetail'));
const Services = lazy(() => import('@/pages/Services'));
const ServiceDetail = lazy(() => import('@/pages/ServiceDetail'));
const Studio = lazy(() => import('@/pages/Studio'));
const Contact = lazy(() => import('@/pages/Contact'));
const Imprint = lazy(() => import('@/pages/legal/Imprint'));
const Privacy = lazy(() => import('@/pages/legal/Privacy'));
const NotFound = lazy(() => import('@/pages/NotFound'));

function PublicLayout() {
  const location = useLocation();
  const navType = useNavigationType();
  const { i18n } = useTranslation();
  const scrollYRef = useRef(0);
  const currentKeyRef = useRef(null);
  const positionsRef = useRef(new Map());

  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (currentKeyRef.current && currentKeyRef.current !== location.key) {
      positionsRef.current.set(currentKeyRef.current, scrollYRef.current);
    }
    currentKeyRef.current = location.key;

    if (location.hash) return;

    const raf = requestAnimationFrame(() => {
      const target = navType === 'POP' ? positionsRef.current.get(location.key) ?? 0 : 0;
      if (window.__lenis) window.__lenis.scrollTo(target, { immediate: true });
      else window.scrollTo(0, target);
    });
    return () => cancelAnimationFrame(raf);
  }, [location.key, location.hash, navType]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lenis = new Lenis({ lerp: 0.1, duration: 1.2, smoothTouch: false });
    window.__lenis = lenis;
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => {
      delete window.__lenis;
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const lang = i18n.language?.startsWith('en') ? 'en' : 'de';
    document.documentElement.lang = lang;
  }, [i18n.language]);

  // Seitenaufruf melden. Zwei rAF, damit react-helmet-async den Titel gesetzt hat.
  // Ohne Einwilligung ist trackPageView ein No-Op.
  useEffect(() => {
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        trackPageView({ path: location.pathname + location.search });
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [location.pathname, location.search]);

  return (
    <>
      <Header />
      <main>
        <Suspense fallback={<div className="h-screen bg-obsidian" />}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageTransition><Home /></PageTransition>} />
              <Route path="/work" element={<PageTransition><Work /></PageTransition>} />
              <Route path="/work/:slug" element={<PageTransition><CaseDetail /></PageTransition>} />
              <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
              <Route path="/services/:slug" element={<PageTransition><ServiceDetail /></PageTransition>} />
              <Route path="/studio" element={<PageTransition><Studio /></PageTransition>} />
              <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
              <Route path="/legal/imprint" element={<PageTransition><Imprint /></PageTransition>} />
              <Route path="/legal/privacy" element={<PageTransition><Privacy /></PageTransition>} />
              <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [loading, setLoading] = useState(() => !sessionStorage.getItem('eb-loaded'));

  const handleLoaderComplete = () => {
    setLoading(false);
    sessionStorage.setItem('eb-loaded', 'true');
  };

  return (
    <HelmetProvider>
      <BrowserRouter>
        <div className="relative public-site">
          <div className="noise-overlay" />
          <CustomCursor />
          <CookieConsent />
          <AnimatePresence>
            {loading && <Loader key="loader" onComplete={handleLoaderComplete} />}
          </AnimatePresence>
          {!loading && <PublicLayout />}
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
